import { SECTION_TABS, type SfSection, type AssistantContext } from "./sections";
import { renderSummary } from "./summary";
import { renderFindings } from "./findings";
import { renderSfChecksheet } from "./checksheet";
import { renderPermissionMap } from "./permission-map";
import { fetchPermissionMap } from "./permission-map-api";
import { renderSnapshots } from "./snapshots";
import { renderUserReconcile } from "./user-reconcile";
import { renderHistory } from "./history";
import { renderSchedule } from "./schedule";
import { renderConnect } from "./connect";
import type { ApiClient } from "../api/client";
import { getConnection } from "./connect-api";
import { runAudit, toFindings } from "./audit-api";
import { listRuns, getRun } from "./history-api";
import { listSnapshots, getSnapshot } from "./snapshot-api";
import { getSchedule, putSchedule, deleteSchedule } from "./schedule-api";
import { fetchActiveUsers } from "./user-reconcile-api";
import { computeSeverityCounts, type SfUser } from "./audit-data";
import type { FindingsSummaryDto } from "./ai-api";
import { createLoadingOverlay } from "../ui/loading-overlay";
import { showToast } from "../ui/toast";
import { renderNotConnected, renderLoadError } from "./empty-states";

export interface SfAuditDeps {
  readonly section: SfSection;
  readonly onNavigate: (section: SfSection) => void;
  readonly openAssistant: (context?: AssistantContext, summary?: FindingsSummaryDto) => void;
  // 実接続チェック用。省略時はサンプルデータにフォールバック（モック表示）。
  readonly apiClient?: ApiClient;
}

export function renderSfAudit(root: HTMLElement, deps: SfAuditDeps): void {
  root.replaceChildren();

  const tablist = document.createElement("div");
  tablist.setAttribute("role", "tablist");
  tablist.setAttribute("aria-label", "Salesforce 監査セクション");
  tablist.className = "mb-6 flex flex-wrap gap-1 border-b border-slate-200";

  for (const tab of SECTION_TABS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("role", "tab");
    const active = tab.section === deps.section;
    btn.setAttribute("aria-selected", active ? "true" : "false");
    btn.className = active
      ? "border-b-2 border-shinonome-700 px-3 py-2 text-sm font-medium text-shinonome-700"
      : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900";
    btn.textContent = tab.label;
    btn.addEventListener("click", () => deps.onNavigate(tab.section));
    tablist.append(btn);
  }

  const panel = document.createElement("div");
  panel.setAttribute("role", "tabpanel");
  root.append(tablist, panel);

  switch (deps.section) {
    case "summary":
      renderSummaryTab(panel, deps);
      break;
    case "findings":
      renderFindingsTab(panel, deps);
      break;
    case "checksheet":
      renderChecksheetTab(panel, deps);
      break;
    case "snapshots":
      renderSnapshotsTab(panel, deps);
      break;
    case "user-reconcile":
      renderUserReconcileTab(panel, deps);
      break;
    case "permission-map":
      renderPermissionMapTab(panel, deps);
      break;
    case "history":
      renderHistoryTab(panel, deps);
      break;
    case "schedule":
      renderScheduleTab(panel, deps);
      break;
    case "connect":
      renderConnect(panel, deps.apiClient !== undefined ? { apiClient: deps.apiClient } : undefined);
      break;
  }
}

// サマリタブ: apiClient があれば接続状態を確認し、connected なら /sf/audit/run を呼ぶ。
// 未接続 / エラー時はサンプルにフォールバック。
function renderSummaryTab(panel: HTMLElement, deps: SfAuditDeps): void {
  if (deps.apiClient === undefined) {
    renderNotConnected(panel, deps.onNavigate);
    return;
  }

  const overlay = createLoadingOverlay({ message: "診断結果を読み込み中..." });
  panel.append(overlay.element);
  overlay.show();

  void loadAndRenderSummary(panel, overlay, deps);
}

async function loadAndRenderSummary(
  panel: HTMLElement,
  overlay: ReturnType<typeof createLoadingOverlay>,
  deps: SfAuditDeps,
): Promise<void> {
  const apiClient = deps.apiClient!;
  try {
    const connection = await getConnection({ apiClient });
    if (connection.status !== "connected") {
      // 未接続/失効 → サンプルは出さず、連携画面への誘導 CTA を表示。
      overlay.hide();
      renderNotConnected(panel, deps.onNavigate);
      return;
    }

    // 表示用の自動呼び出しは read-only（persist を送らない＝ラン量産しない）。
    const resp = await runAudit({ apiClient });
    // notEvaluated はサマリには渡さない（未評価の明示は検出一覧タブのみ。重複表示を避ける）。
    const { findings } = toFindings(resp);
    const counts = computeSeverityCounts(findings);
    overlay.hide();
    renderSummary(panel, {
      onNavigate: deps.onNavigate,
      openAssistant: deps.openAssistant,
      score: resp.healthCheckScore,
      findings,
      counts,
      onRunCheck: () => runCheckAndRerender(panel, deps),
    });
  } catch {
    // 取得失敗 → サンプルにフォールバックせず、エラー + 再試行を表示。
    overlay.hide();
    showToast({ tone: "warning", title: "診断結果の取得に失敗しました", message: "時間をおいて再度お試しください。" });
    renderLoadError(panel, () => renderSummaryTab(panel, deps));
  }
}

// 「設定チェックを実施する」押下時のみ persist:true で実行し、履歴に保存・概要を最新結果で再描画する。
// 失敗時は現在の表示を維持し（panel は再描画しない）、オーバーレイのみ除去して警告トーストを出す。
async function runCheckAndRerender(panel: HTMLElement, deps: SfAuditDeps): Promise<void> {
  const apiClient = deps.apiClient!;
  const overlay = createLoadingOverlay({ message: "設定チェックを実行中..." });
  panel.append(overlay.element);
  overlay.show();
  try {
    const resp = await runAudit({ apiClient }, { persist: true });
    const { findings } = toFindings(resp);
    const counts = computeSeverityCounts(findings);
    overlay.hide();
    renderSummary(panel, {
      onNavigate: deps.onNavigate,
      openAssistant: deps.openAssistant,
      score: resp.healthCheckScore,
      findings,
      counts,
      onRunCheck: () => runCheckAndRerender(panel, deps),
    });
    showToast({ tone: "success", title: "設定チェックを実行しました", message: "結果を履歴に保存しました。" });
  } catch {
    overlay.hide();
    overlay.element.remove(); // 失敗時は現在の表示を維持（panel を再描画しない）。overlay だけ除去。
    showToast({ tone: "warning", title: "設定チェックの実行に失敗しました", message: "時間をおいて再度お試しください。" });
  }
}

// 検出一覧タブ: apiClient があれば接続状態を確認し、connected なら /sf/audit/run を呼ぶ。
// 未接続 / エラー時はサンプルにフォールバック。
function renderFindingsTab(panel: HTMLElement, deps: SfAuditDeps): void {
  if (deps.apiClient === undefined) {
    renderNotConnected(panel, deps.onNavigate);
    return;
  }

  const overlay = createLoadingOverlay({ message: "診断結果を読み込み中..." });
  panel.append(overlay.element);
  overlay.show();

  void loadAndRenderFindings(panel, overlay, deps);
}

async function loadAndRenderFindings(
  panel: HTMLElement,
  overlay: ReturnType<typeof createLoadingOverlay>,
  deps: SfAuditDeps,
): Promise<void> {
  const apiClient = deps.apiClient!;
  try {
    const connection = await getConnection({ apiClient });
    if (connection.status !== "connected") {
      // 未接続/失効 → サンプルは出さず、連携画面への誘導 CTA を表示。
      overlay.hide();
      renderNotConnected(panel, deps.onNavigate);
      return;
    }

    const resp = await runAudit({ apiClient });
    const { findings, notEvaluated } = toFindings(resp);
    overlay.hide();
    renderFindings(panel, {
      openAssistant: deps.openAssistant,
      findings,
      notEvaluated,
    });
  } catch {
    // 取得失敗 → サンプルにフォールバックせず、エラー + 再試行を表示。
    overlay.hide();
    showToast({ tone: "warning", title: "診断結果の取得に失敗しました", message: "時間をおいて再度お試しください。" });
    renderLoadError(panel, () => renderFindingsTab(panel, deps));
  }
}

// チェックシートタブ: 他タブと違い、未接続でも「何をチェックするか」はカタログとして見せる
// （接続前に確認したい情報のため、連携 CTA で隠さない）。診断結果が無い項目は「未実施」として表示され、
// 合格とは区別される。apiClient があれば接続を確認し、connected なら診断結果を反映する。
function renderChecksheetTab(panel: HTMLElement, deps: SfAuditDeps): void {
  if (deps.apiClient === undefined) {
    // 接続確認ができない → カタログのみ（全項目「未実施」）。
    renderSfChecksheet(panel, {});
    return;
  }

  const overlay = createLoadingOverlay({ message: "診断結果を読み込み中..." });
  panel.append(overlay.element);
  overlay.show();

  void loadAndRenderChecksheet(panel, overlay, deps);
}

async function loadAndRenderChecksheet(
  panel: HTMLElement,
  overlay: ReturnType<typeof createLoadingOverlay>,
  deps: SfAuditDeps,
): Promise<void> {
  const apiClient = deps.apiClient!;
  try {
    const connection = await getConnection({ apiClient });
    if (connection.status !== "connected") {
      // 未接続/失効 → 診断結果は出せないが、カタログは見せる（全項目「未実施」）。
      overlay.hide();
      renderSfChecksheet(panel, {});
      return;
    }

    // 表示用の自動呼び出しは read-only（persist を送らない＝ラン量産しない）。
    const resp = await runAudit({ apiClient });
    // notEvaluated は渡さない（coverage.notEvaluated は「カタログ − evaluated」の補集合なので、
    // 「findings に無い = 未評価」と同義。渡しても行は変わらない）。
    const { findings } = toFindings(resp);
    overlay.hide();
    renderSfChecksheet(panel, { findings });
  } catch {
    // 取得失敗 → 診断済みかどうか判断できないため、カタログを「未実施」と偽らずエラー + 再試行を出す。
    overlay.hide();
    showToast({ tone: "warning", title: "診断結果の取得に失敗しました", message: "時間をおいて再度お試しください。" });
    renderLoadError(panel, () => renderChecksheetTab(panel, deps));
  }
}

// 権限マップタブ: 実効権限（プロファイル + 権限セット）は接続済みでなければ確定できないため、
// 未接続ではサンプルを出さず接続導線のみを出す。取得失敗時も部分描画しない
// （権限セットか割当のどちらかが欠けると「重大/高の権限なし」と誤った安心を与えるため）。
function renderPermissionMapTab(panel: HTMLElement, deps: SfAuditDeps): void {
  if (deps.apiClient === undefined) {
    renderNotConnected(panel, deps.onNavigate);
    return;
  }

  const overlay = createLoadingOverlay({ message: "権限マップを読み込み中..." });
  panel.append(overlay.element);
  overlay.show();

  void loadAndRenderPermissionMap(panel, overlay, deps);
}

async function loadAndRenderPermissionMap(
  panel: HTMLElement,
  overlay: ReturnType<typeof createLoadingOverlay>,
  deps: SfAuditDeps,
): Promise<void> {
  const apiClient = deps.apiClient!;
  try {
    const connection = await getConnection({ apiClient });
    if (connection.status !== "connected") {
      overlay.hide();
      renderNotConnected(panel, deps.onNavigate);
      return;
    }
    const map = await fetchPermissionMap({ apiClient });
    overlay.hide();
    renderPermissionMap(panel, { map });
  } catch {
    overlay.hide();
    showToast({ tone: "warning", title: "権限マップの取得に失敗しました", message: "時間をおいて再度お試しください。" });
    renderLoadError(panel, () => renderPermissionMapTab(panel, deps));
  }
}

// 実行履歴タブ: apiClient があれば接続状態を確認し、connected なら /sf/audit/runs を呼ぶ。
// 未接続 / エラー時はサンプルにフォールバック。
function renderHistoryTab(panel: HTMLElement, deps: SfAuditDeps): void {
  if (deps.apiClient === undefined) {
    renderNotConnected(panel, deps.onNavigate);
    return;
  }

  const overlay = createLoadingOverlay({ message: "実行履歴を読み込み中..." });
  panel.append(overlay.element);
  overlay.show();

  void loadAndRenderHistory(panel, overlay, deps);
}

async function loadAndRenderHistory(
  panel: HTMLElement,
  overlay: ReturnType<typeof createLoadingOverlay>,
  deps: SfAuditDeps,
): Promise<void> {
  const apiClient = deps.apiClient!;
  try {
    const connection = await getConnection({ apiClient });
    if (connection.status !== "connected") {
      // 未接続/失効 → サンプルは出さず、連携画面への誘導 CTA を表示。
      overlay.hide();
      renderNotConnected(panel, deps.onNavigate);
      return;
    }

    const runs = await listRuns({ apiClient });
    overlay.hide();
    renderHistory(panel, { runs, loadDetail: (runId) => getRun({ apiClient }, runId) });
  } catch {
    // 取得失敗 → サンプルにフォールバックせず、エラー + 再試行を表示。
    overlay.hide();
    showToast({ tone: "warning", title: "実行履歴の取得に失敗しました", message: "時間をおいて再度お試しください。" });
    renderLoadError(panel, () => renderHistoryTab(panel, deps));
  }
}

// スナップショットタブ: apiClient があれば接続状態を確認し、connected なら /sf/audit/snapshots を呼ぶ。
// 未接続 / エラー / apiClient なし → 従来モック（取得ボタン + pickers + ロールバック）。
function renderSnapshotsTab(panel: HTMLElement, deps: SfAuditDeps): void {
  if (deps.apiClient === undefined) {
    renderNotConnected(panel, deps.onNavigate);
    return;
  }

  const overlay = createLoadingOverlay({ message: "スナップショットを読み込み中..." });
  panel.append(overlay.element);
  overlay.show();

  void loadAndRenderSnapshots(panel, overlay, deps);
}

async function loadAndRenderSnapshots(
  panel: HTMLElement,
  overlay: ReturnType<typeof createLoadingOverlay>,
  deps: SfAuditDeps,
): Promise<void> {
  const apiClient = deps.apiClient!;
  try {
    const connection = await getConnection({ apiClient });
    if (connection.status !== "connected") {
      // 未接続/失効 → サンプルは出さず、連携画面への誘導 CTA を表示。
      overlay.hide();
      renderNotConnected(panel, deps.onNavigate);
      return;
    }

    const snapshots = await listSnapshots({ apiClient });
    overlay.hide();
    renderSnapshots(panel, { snapshots, loadDetail: (id) => getSnapshot({ apiClient }, id) });
  } catch {
    // 取得失敗 → サンプルにフォールバックせず、エラー + 再試行を表示。
    overlay.hide();
    showToast({ tone: "warning", title: "スナップショットの取得に失敗しました", message: "時間をおいて再度お試しください。" });
    renderLoadError(panel, () => renderSnapshotsTab(panel, deps));
  }
}

// スケジュールタブ: apiClient があれば接続状態を確認し、connected なら /sf/audit/schedule を読む。
// connected → config-check カードを実フォーム化（保存=PUT / 解除=DELETE）。
// 未接続 / 取得失敗 / apiClient なし → 従来の全モック（3 ジョブカード + カレンダー）。
function renderScheduleTab(panel: HTMLElement, deps: SfAuditDeps): void {
  if (deps.apiClient === undefined) {
    renderNotConnected(panel, deps.onNavigate);
    return;
  }

  const overlay = createLoadingOverlay({ message: "スケジュールを読み込み中..." });
  panel.append(overlay.element);
  overlay.show();

  void loadAndRenderSchedule(panel, overlay, deps);
}

async function loadAndRenderSchedule(
  panel: HTMLElement,
  overlay: ReturnType<typeof createLoadingOverlay>,
  deps: SfAuditDeps,
): Promise<void> {
  const apiClient = deps.apiClient!;
  try {
    const connection = await getConnection({ apiClient });
    if (connection.status !== "connected") {
      // 未接続/失効 → サンプルは出さず、連携画面への誘導 CTA を表示（接続前提のため）。
      overlay.hide();
      renderNotConnected(panel, deps.onNavigate);
      return;
    }

    const schedule = await getSchedule({ apiClient });
    overlay.hide();
    renderSchedule(panel, {
      schedule,
      onSave: async (body) => {
        try {
          await putSchedule({ apiClient }, body);
          showToast({ tone: "success", title: "スケジュールを保存しました", message: "設定チェックを定期実行します。" });
          await reloadSchedule(panel, deps);
        } catch {
          showToast({ tone: "warning", title: "スケジュールの保存に失敗しました", message: "時間をおいて再度お試しください。" });
        }
      },
      onDelete: async () => {
        try {
          await deleteSchedule({ apiClient });
          showToast({ tone: "info", title: "スケジュールを解除しました", message: "定期実行を停止しました。" });
          await reloadSchedule(panel, deps);
        } catch {
          showToast({ tone: "warning", title: "スケジュールの解除に失敗しました", message: "時間をおいて再度お試しください。" });
        }
      },
    });
  } catch {
    // 取得失敗 → サンプルにフォールバックせず、エラー + 再試行を表示。
    overlay.hide();
    showToast({ tone: "warning", title: "スケジュールの取得に失敗しました", message: "時間をおいて再度お試しください。" });
    renderLoadError(panel, () => renderScheduleTab(panel, deps));
  }
}

// 保存 / 解除の後にスケジュールを再取得して再描画する（オーバーレイ付き）。
async function reloadSchedule(panel: HTMLElement, deps: SfAuditDeps): Promise<void> {
  const overlay = createLoadingOverlay({ message: "スケジュールを読み込み中..." });
  panel.append(overlay.element);
  overlay.show();
  await loadAndRenderSchedule(panel, overlay, deps);
}

// ユーザー棚卸しタブ: apiClient があれば接続状態を確認し、connected なら /sf/users/active を呼ぶ。
// 取得した実 SF アクティブユーザーで突合する（従業員名簿 CSV はブラウザ内処理・送信しない）。
// 未接続 / 取得失敗 / apiClient なし → 従来モック（SF_USERS で突合）。
function renderUserReconcileTab(panel: HTMLElement, deps: SfAuditDeps): void {
  if (deps.apiClient === undefined) {
    renderNotConnected(panel, deps.onNavigate);
    return;
  }

  const overlay = createLoadingOverlay({ message: "Salesforce ユーザーを読み込み中..." });
  panel.append(overlay.element);
  overlay.show();

  void loadAndRenderUserReconcile(panel, overlay, deps);
}

async function loadAndRenderUserReconcile(
  panel: HTMLElement,
  overlay: ReturnType<typeof createLoadingOverlay>,
  deps: SfAuditDeps,
): Promise<void> {
  const apiClient = deps.apiClient!;
  try {
    const connection = await getConnection({ apiClient });
    if (connection.status !== "connected") {
      // 未接続/失効 → サンプルは出さず、連携画面への誘導 CTA を表示。
      overlay.hide();
      renderNotConnected(panel, deps.onNavigate);
      return;
    }

    const dtos = await fetchActiveUsers({ apiClient });
    // SfActiveUserDto → SfUser（active は API が返すアクティブ一覧なので常に true）。
    const sfUsers: readonly SfUser[] = dtos.map((d) => ({
      username: d.username,
      email: d.email,
      name: d.name,
      profile: d.profile,
      active: true,
      lastLogin: d.lastLoginDate ?? "—",
    }));
    overlay.hide();
    renderUserReconcile(panel, { sfUsers });
  } catch {
    // 取得失敗 → サンプルにフォールバックせず、エラー + 再試行を表示。
    overlay.hide();
    showToast({ tone: "warning", title: "Salesforce ユーザーの取得に失敗しました", message: "時間をおいて再度お試しください。" });
    renderLoadError(panel, () => renderUserReconcileTab(panel, deps));
  }
}
